import React, { createContext, useContext, useMemo, useState } from 'react';
import Api from 'talent_search/lib/api';
import type {
  Paths,
  SavedSearchGroup,
  SavedSearchItem,
} from 'talent_search/shared/types';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import type { RediscoveryProps } from 'talent_matching/types';

export interface RediscoveryTabContextValue {
  apiClient: Api;
  paths: Paths;
  milestones: Item[];
  currencies: Item[];
  savedSearchOptions: SavedSearchGroup[];
  unfilteredCount: number;
  savedFilterSet: SavedSearchItem | null;
  setSavedFilterSet: (ss: SavedSearchItem | null) => void;
}

const RediscoveryTabContext = createContext<
  RediscoveryTabContextValue | undefined
>(undefined);

export const useRediscoveryTabContext = () => {
  const context = useContext(RediscoveryTabContext);
  if (!context) {
    throw new Error(
      'useRediscoveryTabContext must be used within a RediscoveryTabContextProvider'
    );
  }
  return context;
};

type RediscoveryTabContextProviderProps = {
  rediscoveryProps: RediscoveryProps;
  children: React.ReactNode;
};

export const RediscoveryTabContextProvider = ({
  rediscoveryProps,
  children,
}: RediscoveryTabContextProviderProps) => {
  const [savedFilterSet, setSavedFilterSet] = useState<SavedSearchItem | null>(
    null
  );

  const apiClient = useMemo(() => {
    return new Api(rediscoveryProps.paths);
  }, [rediscoveryProps?.paths]);

  const contextValue: RediscoveryTabContextValue = useMemo(
    () => ({
      apiClient,
      paths: rediscoveryProps.paths,
      milestones: rediscoveryProps.milestones,
      currencies: rediscoveryProps.currencies,
      savedSearchOptions: rediscoveryProps.savedSearchOptions ?? [],
      unfilteredCount: rediscoveryProps.unfilteredCount,
      savedFilterSet,
      setSavedFilterSet,
    }),
    [rediscoveryProps, apiClient, savedFilterSet]
  );

  return (
    <RediscoveryTabContext.Provider value={contextValue}>
      {children}
    </RediscoveryTabContext.Provider>
  );
};
